import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Footer from './Footer';
import './TechTheme.css';

const DokimasiaPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <>
      <Helmet>
        <title>DOKIMASIA.AI — EU AI Act Compliance Platform | PROTOS AI Agency</title>
        <meta name="description" content="DOKIMASIA.AI is Europe's first automated EU AI Act compliance platform. AI-powered risk classification, Annex IV documentation, FRIA generation, legal advisor. Built by PROTOS AI Agency. Free to use." />
        <meta name="keywords" content="EU AI Act, AI Act compliance, AI risk classification, Annex III, Annex IV technical documentation, FRIA, fundamental rights impact assessment, AI regulation Europe, AI compliance tool, AI governance, high-risk AI systems, conformity assessment, CE marking AI, AI transparency, AI Act penalties, AI Act deadline 2026, DOKIMASIA, PROTOS AI Agency, Regulation 2024/1689" />
        <link rel="canonical" href="https://ai-agency.gr/dokimasia" />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph */}
        <meta property="og:title" content="DOKIMASIA.AI — EU AI Act Compliance Platform" />
        <meta property="og:description" content="Europe's first automated AI Act compliance platform. Risk classification, Annex IV documentation, FRIA, legal advisor. 24 EU languages. Free." />
        <meta property="og:url" content="https://ai-agency.gr/dokimasia" />
        <meta property="og:type" content="website" />
        
        {/* SoftwareApplication Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "DOKIMASIA.AI",
            "url": "https://dokimasia.ai",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "description": "Europe's first automated EU AI Act compliance platform with AI-powered risk classification, Annex IV technical documentation generation, Fundamental Rights Impact Assessment, and legal advisory based on 241 articles of Regulation 2024/1689. Available in 24 official EU languages.",
            "author": {
              "@type": "Organization",
              "name": "PROTOS AI Agency IKE",
              "url": "https://ai-agency.gr"
            },
            "offers": [
              {"@type": "Offer", "price": "0", "priceCurrency": "EUR", "name": "Free"},
              {"@type": "Offer", "price": "99", "priceCurrency": "EUR", "name": "Professional"},
              {"@type": "Offer", "price": "349", "priceCurrency": "EUR", "name": "Business"},
              {"@type": "Offer", "price": "999", "priceCurrency": "EUR", "name": "Enterprise"}
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white">
        {/* Hero Section */}
        <section className="relative py-20 px-4 text-center overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          <div className="relative z-10 max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-white">DOKIMASIA</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">.AI</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Europe's first automated EU AI Act compliance platform. Classify your AI systems, generate Annex IV documentation, conduct Fundamental Rights Impact Assessments, and get AI-powered legal guidance — all based on the actual regulation text.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="https://dokimasia.ai" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Try DOKIMASIA.AI Free
              </a>
              <a 
                href="https://dokimasia.ai/pricing" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-transparent border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 font-bold py-4 px-8 rounded-full transition-all duration-300"
              >
                View Pricing
              </a>
            </div>
          </div>
        </section>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          
          {/* What is DOKIMASIA */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">What is DOKIMASIA.AI?</h2>
            <p className="text-lg text-gray-300 mb-4 leading-relaxed">
              DOKIMASIA.AI is a SaaS platform that automates compliance with the EU AI Act (Regulation 2024/1689). Developed by <a href="https://ai-agency.gr" className="text-cyan-400 hover:text-cyan-300">PROTOS AI Agency IKE</a> in Thessaloniki, Greece, it is the first European tool that covers the complete AI Act compliance lifecycle — from initial risk assessment to finished regulatory documentation.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              The platform ingests 241 articles and annexes from the official EU AI Act text (sourced from EUR-Lex) into a legal knowledge base, enabling precise risk classification, context-aware document generation, and grounded legal advisory.
            </p>
            <p className="text-gray-400 mt-4 italic">
              The name <em>dokimasia</em> (δοκιμασία) comes from ancient Athens — the official fitness examination every public official had to pass before assuming office. Today, DOKIMASIA.AI examines your AI systems.
            </p>
          </section>

          {/* Statistics */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white/5 backdrop-blur-sm border border-cyan-500/30 rounded-xl p-8 text-center">
              <div className="text-5xl font-bold text-cyan-400 mb-2">241</div>
              <div className="text-gray-400">EU AI Act Articles in RAG Database</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-blue-500/30 rounded-xl p-8 text-center">
              <div className="text-5xl font-bold text-blue-400 mb-2">24</div>
              <div className="text-gray-400">Official EU Languages Supported</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-purple-500/30 rounded-xl p-8 text-center">
              <div className="text-5xl font-bold text-purple-400 mb-2">10x</div>
              <div className="text-gray-400">Cheaper Than Enterprise Alternatives</div>
            </div>
          </div>

          {/* Core Features */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">Core Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:border-cyan-500/30 transition-all">
                <h3 className="text-xl font-bold text-cyan-400 mb-4">AI Risk Classification</h3>
                <p className="text-gray-300 leading-relaxed">
                  Automated classification under Annex III of the EU AI Act. Covers all eight high-risk categories: biometric identification, critical infrastructure, education and training, employment, access to essential services, law enforcement, migration, and administration of justice.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:border-blue-500/30 transition-all">
                <h3 className="text-xl font-bold text-blue-400 mb-4">Annex IV Technical Documentation</h3>
                <p className="text-gray-300 leading-relaxed">
                  AI-powered generation of complete technical documentation as required by Article 11 and Annex IV. Pre-filled based on your specific AI system — covering design specifications, data governance, human oversight, risk management, and post-market monitoring.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:border-purple-500/30 transition-all">
                <h3 className="text-xl font-bold text-purple-400 mb-4">Fundamental Rights Impact Assessment</h3>
                <p className="text-gray-300 leading-relaxed">
                  FRIA generation as required by Article 27 for deployers of high-risk AI systems. Assesses impact across all applicable fundamental rights: non-discrimination, privacy, data protection, human dignity, effective remedy, and workers' rights.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:border-pink-500/30 transition-all">
                <h3 className="text-xl font-bold text-pink-400 mb-4">AI-Powered Legal Advisor</h3>
                <p className="text-gray-300 leading-relaxed">
                  RAG-based legal chatbot grounded in the full EU AI Act text. Answers questions about obligations, conformity assessment, CE marking, prohibited practices (Article 5), transparency (Article 50), and penalties — with article-level citations.
                </p>
              </div>
            </div>
          </section>

          {/* Who Needs This */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">Who Needs EU AI Act Compliance?</h2>
            
            <div className="space-y-8">
              <div className="bg-white/5 rounded-xl p-6 border-l-4 border-cyan-500">
                <h3 className="text-xl font-bold text-white mb-3">Compliance Officers and Data Protection Officers</h3>
                <p className="text-gray-300">
                  Every organisation deploying AI systems in the EU must assess whether the AI Act applies. DOKIMASIA.AI provides instant risk classification and generates the documentation required for high-risk systems.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-white mb-3">Law Firms and Legal Consultants</h3>
                <p className="text-gray-300">
                  EU AI Act compliance advisory is a growing practice area. DOKIMASIA.AI accelerates case work by generating first drafts in minutes. Business tier offers white-label reports.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-white mb-3">AI Providers and Technology Companies</h3>
                <p className="text-gray-300">
                  Providers placing AI systems on the EU market face the most demanding obligations: technical documentation, conformity assessment, quality management, EU database registration, and CE marking.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border-l-4 border-pink-500">
                <h3 className="text-xl font-bold text-white mb-3">Public Administrations</h3>
                <p className="text-gray-300">
                  Government agencies procuring or deploying AI systems must ensure compliance and conduct Fundamental Rights Impact Assessments. Available in all 24 official EU languages.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-white mb-3">AI Startups Entering the EU Market</h3>
                <p className="text-gray-300">
                  Non-EU companies placing AI systems on the European market need compliance documentation. DOKIMASIA.AI provides the fastest path at a fraction of traditional consultancy costs.
                </p>
              </div>
            </div>
          </section>

          {/* Deadline Warning */}
          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-2 border-red-500/50 rounded-2xl p-8 text-center mb-16">
            <h3 className="text-3xl font-bold text-red-400 mb-4">August 2, 2026</h3>
            <p className="text-lg text-gray-200">
              Full enforcement of high-risk AI system requirements. Penalties: up to €35 million or 7% of global annual turnover.
            </p>
          </div>

          {/* Timeline */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">EU AI Act Compliance Timeline</h2>
            <div className="bg-white/5 rounded-xl p-8 border border-white/10">
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 font-bold min-w-[140px]">Feb 2, 2025</span>
                  <span>Prohibited AI practices enforceable (Article 5). AI literacy obligations in effect (Article 4).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold min-w-[140px]">Aug 2, 2025</span>
                  <span>GPAI model obligations enforceable. Governance provisions apply.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-400 font-bold min-w-[140px]">Aug 2, 2026</span>
                  <span>Full enforcement of high-risk AI system requirements (Articles 6-27). Conformity assessments required. EU database registration mandatory.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-pink-400 font-bold min-w-[140px]">Aug 2, 2027</span>
                  <span>High-risk AI in products covered by EU harmonisation legislation (Annex I, Section A).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold min-w-[140px]">Dec 31, 2027</span>
                  <span>Backstop deadline even if harmonised standards are delayed.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Pricing */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Pricing</h2>
            <p className="text-gray-300 mb-8">Enterprise-grade AI Act compliance tools at a fraction of the cost. While competitors charge five-figure annual contracts, DOKIMASIA.AI starts free.</p>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold text-white mb-2">Free</h3>
                <div className="text-4xl font-bold text-cyan-400 mb-4">€0</div>
                <p className="text-gray-400 text-sm">Unlimited risk classifications. 1 legal advisor query per day. No credit card required.</p>
              </div>

              <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border-2 border-cyan-500/50 rounded-xl p-6 text-center transform scale-105">
                <div className="bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-2">POPULAR</div>
                <h3 className="text-xl font-bold text-white mb-2">Professional</h3>
                <div className="text-4xl font-bold text-cyan-400 mb-1">€99<span className="text-lg">/mo</span></div>
                <p className="text-gray-400 text-sm mb-4">Unlimited legal advisor. Document generator. PDF export. 5 AI systems. 7-day free trial.</p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold text-white mb-2">Business</h3>
                <div className="text-4xl font-bold text-blue-400 mb-4">€349<span className="text-lg">/mo</span></div>
                <p className="text-gray-400 text-sm">25 AI systems. 5 team members. White-label reports. API access. Priority support.</p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold text-white mb-2">Enterprise</h3>
                <div className="text-4xl font-bold text-purple-400 mb-4">€999<span className="text-lg">/mo</span></div>
                <p className="text-gray-400 text-sm">Unlimited everything. Dedicated manager. SLA. On-premise option. Custom integrations.</p>
              </div>
            </div>
          </section>

          {/* EU Credentials */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Built with EU Credentials</h2>
            <p className="text-gray-300 mb-6">
              DOKIMASIA.AI is developed by <a href="https://ai-agency.gr" className="text-cyan-400 hover:text-cyan-300">PROTOS AI Agency IKE</a>, a Greek AI company specialising in regulatory AI for the European public sector. Our credentials:
            </p>
            <div className="bg-white/5 rounded-xl p-8 border border-cyan-500/20">
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400">•</span>
                  <span><strong>EU AI Pact Signatory</strong> — Official commitment to early AI Act implementation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400">•</span>
                  <span><strong>EU AI Alliance Member</strong> — Active participant in European AI policy community</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400">•</span>
                  <span><strong>Registered EU Expert</strong> — ID: EX2025D1322797, Artificial Intelligence for public and critical infrastructure</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400">•</span>
                  <span><strong>Official AI Partner of the Holy Community of Mount Athos</strong></span>
                </li>
              </ul>
            </div>
            <p className="text-gray-300 mt-6">
              PROTOS also operates <a href="https://nomokratia.ai" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">NOMOKRATIA.AI</a> (Greek legal AI, 3.5M+ documents), deployed Whisper Police with the Hellenic Police (98.9% accuracy, 50,000+ work hours saved), and coordinates €24M in EU-funded consortia: CyberSentinel (Digital Europe, €5M) and IANUS (Horizon Europe, €19M).
            </p>
          </section>

          {/* Technical Architecture */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Technical Architecture</h2>
            <div className="bg-white/5 rounded-xl p-8 border border-white/10">
              <ul className="space-y-4 text-gray-300">
                <li><strong className="text-white">RAG Knowledge Base:</strong> 241 articles and annexes from the EU AI Act, parsed from EUR-Lex, embedded in ChromaDB</li>
                <li><strong className="text-white">AI Engine:</strong> Anthropic Claude (Sonnet) for document generation and legal advisory, with DPA and EU SCCs</li>
                <li><strong className="text-white">Infrastructure:</strong> Hetzner Cloud, Falkenstein, Germany — all data remains in the EU</li>
                <li><strong className="text-white">Stack:</strong> React + Tailwind CSS (frontend), FastAPI + MongoDB (backend), Nginx + Let's Encrypt SSL</li>
                <li><strong className="text-white">Compliance:</strong> GDPR-compliant, Cookie consent, AI transparency disclaimer, data export (Art. 20), right to erasure (Art. 17)</li>
              </ul>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-cyan-400 mb-3">Does DOKIMASIA.AI replace legal advice?</h3>
                <p className="text-gray-300">
                  No. DOKIMASIA.AI generates AI-assisted compliance guidance and document drafts based on the actual EU AI Act text. All outputs should be reviewed by a qualified legal professional before submission to regulatory authorities.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-cyan-400 mb-3">How accurate is the risk classification?</h3>
                <p className="text-gray-300">
                  The risk classification is rule-based, matching your AI system description against all eight Annex III high-risk categories. It identifies applicable categories, relevant articles, and role-specific obligations. Edge cases should be reviewed with legal counsel.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-cyan-400 mb-3">Is my data safe?</h3>
                <p className="text-gray-300">
                  All data is stored on EU-based servers (Hetzner, Germany). API queries to Anthropic Claude are covered by a Data Processing Addendum with EU Standard Contractual Clauses and the EU-US Data Privacy Framework. DOKIMASIA.AI is GDPR-compliant with data export and deletion capabilities.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-cyan-400 mb-3">Can I use DOKIMASIA.AI in my language?</h3>
                <p className="text-gray-300">
                  Yes. The platform interface is available in all 24 official EU languages: English, German, Greek, French, Spanish, Italian, Portuguese, Dutch, Polish, Romanian, Czech, Hungarian, Swedish, Danish, Finnish, Slovak, Slovenian, Bulgarian, Croatian, Lithuanian, Latvian, Estonian, Irish, and Maltese.
                </p>
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <div className="text-center py-16 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 rounded-2xl border border-cyan-500/30">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready for the EU AI Act?</h2>
            <p className="text-xl text-gray-300 mb-8">Start for free. No credit card required. Classify your first AI system in 2 minutes.</p>
            <a 
              href="https://dokimasia.ai" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-4 px-10 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Try DOKIMASIA.AI Free
            </a>
          </div>

        </div>

        <Footer />
      </div>
    </>
  );
};

export default DokimasiaPage;
